/**
 * Who sees what, when — pure, so every rule is unit-tested (#1838).
 *
 * Decisions (Corbin, 10/9):
 *   Spotlight  Help Desk + Tasks. Everyone sees it on first login. It comes
 *              back WEEKLY, up to 4 more times, for anyone with no use in the
 *              last 30 days. One real use after it first showed stops it.
 *              Techs get the Help Desk spotlight only, never mid-visit.
 *   Intro card Once per person, first time they land where the feature
 *              lives. Never two at once, never before the spotlight.
 *   Dots       Office only (no dots on the iPad). At most 3 on a screen, in
 *              role order. Used once = gone for good. Not now = 14 days.
 *              "Don't show tips" turns them all off for that person.
 */
import { FEATURES, type TeachFeature, type TeachRole } from "./features";

export interface FeatureUse {
    first: string;
    last: string;
    count: number;
}

export interface TeachStateData {
    on: boolean;
    seen: Record<string, string>;
    snoozed: Record<string, string>;
    tipsOff: boolean;
    spotlight: Record<string, string[]>;
    used: Record<string, FeatureUse>;
}

export const EMPTY_TEACH_STATE: TeachStateData = {
    on: false,
    seen: {},
    snoozed: {},
    tipsOff: false,
    spotlight: {},
    used: {},
};

export const MAX_DOTS = 3;
export const SPOTLIGHT_REPEATS = 4;
export const SPOTLIGHT_EVERY_DAYS = 7;
export const SPOTLIGHT_QUIET_IF_USED_DAYS = 30;

const DAY = 86_400_000;

function t(iso: string | undefined | null): number {
    if (!iso) return NaN;
    return new Date(iso).getTime();
}

export function forRole(f: TeachFeature, role: TeachRole): boolean {
    return f.roles.includes(role);
}

/**
 * Should the spotlight for `key` show now?
 *  - never shown → yes (first login after the switch is on)
 *  - used after it first showed → never again
 *  - used in the last 30 days → not now
 *  - shown 1 + 4 times already → done
 *  - last showing under 7 days ago → not yet
 */
export function spotlightDue(key: string, s: TeachStateData, now: Date): boolean {
    const shown = (s.spotlight[key] ?? []).map(t).filter((n) => !Number.isNaN(n));
    if (shown.length === 0) return true;
    const use = s.used[key];
    const firstShown = Math.min(...shown);
    const lastShown = Math.max(...shown);
    if (use) {
        const lastUse = t(use.last);
        if (lastUse >= firstShown) return false;
        if (now.getTime() - lastUse < SPOTLIGHT_QUIET_IF_USED_DAYS * DAY) return false;
    }
    if (shown.length >= 1 + SPOTLIGHT_REPEATS) return false;
    return now.getTime() - lastShown >= SPOTLIGHT_EVERY_DAYS * DAY;
}

/**
 * The spotlight has run its course: the first showing plus all 4 repeats.
 * Until then its button gets no dot — the spotlight is the louder teacher,
 * and "then the dot stays" (approved mockup). A use ends both anyway.
 */
export function spotlightSeriesDone(key: string, s: TeachStateData): boolean {
    return (s.spotlight[key] ?? []).length >= 1 + SPOTLIGHT_REPEATS;
}

/** The spotlight cards due for this person, in order (Help Desk, then Tasks). */
export function spotlightsDue(s: TeachStateData, role: TeachRole, now: Date): TeachFeature[] {
    if (!s.on) return [];
    return FEATURES.filter((f) => f.spotlight && forRole(f, role) && spotlightDue(f.key, s, now)).sort(
        (a, b) => (a.spotlight?.order ?? 0) - (b.spotlight?.order ?? 0),
    );
}

/** May the intro card for `key` show to this person? (The screen decides "here, now".) */
export function introEligible(key: string, s: TeachStateData, role: TeachRole, features = FEATURES): boolean {
    if (!s.on) return false;
    const f = features.find((x) => x.key === key);
    if (!f?.intro || !forRole(f, role)) return false;
    return !s.seen[key];
}

/**
 * Which tip keys get a dot, given the `data-teach` keys present on screen.
 * Role order first, then newest feature. Spotlight features wait until their
 * spotlight series is finished (spotlightSeriesDone), so the two never point
 * at the same button.
 */
export function dotsFor(
    present: Iterable<string>,
    s: TeachStateData,
    role: TeachRole,
    now: Date,
    features = FEATURES,
): string[] {
    if (!s.on || s.tipsOff || role !== "office") return [];
    const here = new Set(present);
    return features
        .filter((f) => f.tip && here.has(f.key) && forRole(f, role))
        .filter((f) => !s.used[f.key])
        .filter((f) => {
            const until = t(s.snoozed[f.key]);
            return Number.isNaN(until) || until <= now.getTime();
        })
        .filter((f) => !(f.spotlight && !spotlightSeriesDone(f.key, s)))
        .sort((a, b) => (a.tip?.order ?? 99) - (b.tip?.order ?? 99) || b.shipped.localeCompare(a.shipped))
        .slice(0, MAX_DOTS)
        .map((f) => f.key);
}
